import React, {useState, useEffect, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import Modal, {ModalFrame as Frame} from './Modal';
import ToastContainer from './ToastContainer';
import {useToast} from '../hooks/useToast';
import vfsClient from '../utils/vfsClient';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import {changedServiceKeys} from '../utils/changedServices';
import {undone} from '../analytics/journey';
import CommitDiffView from './CommitDiffView';
import PublishedReleases from './PublishedReleases';
import useActorNames from '../hooks/useActorNames';
import useTabSelectionUrl from '../hooks/useTabSelectionUrl';
import './HistoryPanel.css';

/**
 * HistoryPanel — checkpoint/history UI for a vfs-backed project's Code door
 * (task #25/#6, "Granular checkpointing & one-click rewind"). Talks directly
 * to vfs-service's real git-backed history API via vfsClient.js; see that
 * file's doc comments for the exact request/response shapes.
 *
 * "Rewind to here" calls vfs-service's whole-project rollback
 * (`vfsClient.rollbackAll` -> `POST /history/rollback-all`): the project as it
 * was at that commit — every service (each has its own repo) back to its last
 * commit at that moment, a service added later left as it is — through the
 * same live-collaborative-doc-safe mechanism a single-file rollback uses (an
 * open editor on any restored file is handled correctly, not silently
 * overwritten), binary files byte for byte, ONE new commit per service.
 * Changes not committed yet are committed first. Genuinely non-destructive:
 * nothing is ever force-reset or lost, old commits stay in the log, and Redo
 * puts back exactly what was there before (the answer's `previous`).
 *
 * The log also lists what the project did with remotes (`kind: 'remote'`:
 * "Pushed with … to origin/main"); those rows are not checkpoints (their sha
 * may be the commit pushed, or none), so they have no "Rewind to here". The
 * newest checkpoint is how the project is now: it has none either.
 */
const isCheckpoint = (entry) => entry?.kind !== 'remote' && typeof entry?.sha === 'string' && entry.sha.length > 0;
const shortSha = (sha) => (typeof sha === 'string' ? sha.slice(0, 10) : '');
const repoLabel = (repo) => (typeof repo === 'string' ? repo.replace(/^services\//, '') : '');

/** "Rewound 3 files in web and api to 1a2b3c4d5e" (+ the services left as they are). */
export function rewindSummary(result, sha, {isRedo = false} = {}) {
  const files = result?.restoredPaths?.length || 0;
  const repos = (result?.commits || []).filter((c) => c.sha).map((c) => repoLabel(c.repo));
  const where = repos.length ? ` in ${repos.join(' and ')}` : '';
  const base = `${isRedo ? 'Redone' : 'Rewound'} ${files} file${files === 1 ? '' : 's'}${where}${isRedo ? '' : ` to ${shortSha(sha)}`}`;
  const left = (result?.untouched || []).map((u) => repoLabel(u.repo));
  return left.length ? `${base}. Left as ${left.length === 1 ? 'it is' : 'they are'} (added later): ${left.join(', ')}` : base;
}
function formatRelativeTime(isoString) {
  if (!isoString) {
    return '';
  }
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) {
    return isoString;
  }
  const diffMs = Date.now() - then;
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 5) {
    return 'just now';
  }
  if (diffSec < 60) {
    return `${diffSec}s ago`;
  }
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) {
    return `${diffHour}h ago`;
  }
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 30) {
    return `${diffDay}d ago`;
  }
  return new Date(isoString).toLocaleDateString();
}

// `embedded`: the content alone, as the Timeline tab of the project's History (components/workspace/WorkspaceView.jsx);
// `focusSha`: that commit opened with its diff.
const HistoryPanel = ({isOpen, onClose, projectId, serviceName, embedded = false, focusSha = null}) => {
  const {toasts, showToast, removeToast} = useToast();
  // `user:<sub>` → the person's name (the project's people), for the rows and the opened commit's header.
  const actorOf = useActorNames(isOpen ? projectId : null);

  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [selectedSha, setSelectedSha] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [diffText, setDiffText] = useState('');
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState(null);

  const [confirmRewind, setConfirmRewind] = useState(null); // {sha, message} | null
  const [rewinding, setRewinding] = useState(false);
  const [rewindStatus, setRewindStatus] = useState('');
  // Redo, leveraging the exact same real, atomic rollbackAll mechanism as
  // rewind: "redo" is just "rewind forward again" to the commit that was
  // HEAD right before the last rewind. Standard undo/redo-stack semantics —
  // any other rewind/checkpoint clears it, since jumping back to a
  // pre-rewind state would silently discard whatever happened since.
  const [redoTarget, setRedoTarget] = useState(null); // {sha, targets} | null

  const [checkpointMessage, setCheckpointMessage] = useState('');
  const [checkpointing, setCheckpointing] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!projectId) {
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const log = await vfsClient.getHistory(projectId);
      setCommits(Array.isArray(log) ? log : []);
      setLoaded(true);
    } catch (error) {
      setLoadError(error.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
      // Reset per-open UI state so a stale diff/selection from a previous
      // open doesn't linger.
      setSelectedSha(null);
      setDiffText('');
      setDiffError(null);
      setConfirmRewind(null);
      setRewindStatus('');
      setRedoTarget(null);
    }
  }, [isOpen, loadHistory]);

  // Opened on one commit (an item of the project's History card): its diff, once the list is there.
  const focusedRef = useRef(null);
  useEffect(() => {
    if (!isOpen || !focusSha || focusedRef.current === focusSha || !commits.some((c) => c.sha === focusSha)) { return; }
    focusedRef.current = focusSha;
    handleSelectCommit(focusSha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, focusSha, commits]);

  // The commit open in the URL (`?tab=changes&sub=timeline&commit=<sha>`), in the workspace: once the list is read and a
  // commit asked for is open (one that is not in the list is cleaned).
  const focusPending = Boolean(focusSha) && focusedRef.current !== focusSha && commits.some((c) => c.sha === focusSha);
  useTabSelectionUrl('changes', { commit: selectedSha }, { ready: embedded && isOpen && loaded && !focusPending });

  if (!isOpen) {
    return null;
  }

  const handleSelectCommit = async (sha) => {
    if (selectedSha === sha) {
      setSelectedSha(null);
      setDiffText('');
      setDiffError(null);
      return;
    }
    setSelectedSha(sha);
    setDiffText('');
    setDiffError(null);
    setDiffLoading(true);
    try {
      const diff = await vfsClient.getCommitDiff(projectId, sha);
      setDiffText(diff || '');
    } catch (error) {
      setDiffError(`Failed to load diff: ${error.message}`);
    } finally {
      setDiffLoading(false);
    }
  };

  const handleCheckpoint = async () => {
    if (!projectId || checkpointing) {
      return;
    }
    setCheckpointing(true);
    try {
      const sha = await vfsClient.commitCheckpoint(projectId, checkpointMessage.trim() || undefined);
      if (sha) {
        showToast(`Checkpoint created (${sha.slice(0, 10)})`, 'success');
        // New work — a pending redo would jump back to before this checkpoint,
        // silently discarding it. Standard undo/redo-stack semantics: any new
        // action clears the redo stack.
        setRedoTarget(null);
      } else {
        showToast('Nothing to checkpoint — no uncommitted changes', 'info');
      }
      setCheckpointMessage('');
      await loadHistory();
    } catch (error) {
      showToast(`Checkpoint failed: ${error.message}`, 'error');
    } finally {
      setCheckpointing(false);
    }
  };

  const performRewind = async (sha, {isRedo = false, targets = null} = {}) => {
    const checkpoints = commits.filter(isCheckpoint);
    const result = await vfsClient.rollbackAll(projectId, sha, targets ? {targets} : {});
    const restoredPaths = result?.restoredPaths || [];

    if (restoredPaths.length === 0) {
      showToast('Already at this state — nothing to rewind', 'info');
      // The rewind's own "checkpoint before rollback" may still have landed: the list shows it.
      await loadHistory();
      return;
    }
    // Back to the version just before the latest is undoing the last change; anything further back is an older one.
    if (!isRedo) { undone(sha === checkpoints[1]?.sha ? 'last' : 'older'); }

    // Pull the current service's files back into IndexedDB (the fast local
    // cache Monaco/the file tree actually read from) so the rewind is
    // immediately visible without a manual reload — `fromVfs: true` marks
    // these as a pull so indexedDbFileStorage does NOT write them straight
    // back to vfs-service (that would ping-pong the rollback we just did).
    // Every service the rewind changed (it can move several); an older vfs-service names none: the one open here.
    // By the service's path (`services/web`), the key of the editor's cache: a bare `web` is a 404 on GET /tree.
    const changed = changedServiceKeys(result?.commits);
    const toRefresh = result?.commits ? changed : [serviceName].filter(Boolean);
    if (toRefresh.length) {
      setRewindStatus('Refreshing editor content…');
      try {
        for (const name of toRefresh) {
          // eslint-disable-next-line no-await-in-loop
          const files = await vfsClient.fetchServiceFiles(projectId, name);
          const entries = Object.entries(files);
          for (let i = 0; i < entries.length; i += 1) {
            const [relPath, content] = entries[i];
            // eslint-disable-next-line no-await-in-loop
            await indexedDbFileStorage.saveFile(projectId, name, relPath, content, {fromVfs: true});
          }
        }
      } catch (error) {
        // Non-fatal: the server-side rewind already succeeded; only the
        // local editor cache refresh failed. Surface it but don't treat
        // the whole rewind as failed.
        showToast(`Rewind succeeded, but refreshing the editor view failed: ${error.message}. Reload to see the restored files.`, 'warning');
      }

      // Tell RepositoryTree/MonacoFileEditor to reload from IndexedDB —
      // the same signal a git pull completing already uses.
      toRefresh.forEach((name) => {
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: {projectId, serviceName: name, success: true},
        }));
      });
    }

    showToast(rewindSummary(result, sha, {isRedo}), 'success');

    // Any successful rewind/redo clears whatever redo target existed before
    // (standard undo/redo-stack semantics), then sets a new one — UNLESS this
    // call itself IS a redo, in which case there's nothing left to redo
    // further forward. The redo puts every repo back exactly where it was
    // (`previous`: each repo's HEAD just before this rewind).
    const previous = result?.previous && Object.keys(result.previous).length ? result.previous : null;
    setRedoTarget(isRedo || !previous ? null : {sha: Object.values(previous)[0], targets: previous});

    await loadHistory();
  };

  const handleConfirmRewind = async () => {
    if (!confirmRewind || rewinding) {
      return;
    }
    const {sha} = confirmRewind;
    setRewinding(true);
    setRewindStatus('Rewinding…');
    try {
      await performRewind(sha);
      setConfirmRewind(null);
    } catch (error) {
      showToast(`Rewind failed: ${error.message}`, 'error');
    } finally {
      setRewinding(false);
      setRewindStatus('');
    }
  };

  const handleRedo = async () => {
    if (!redoTarget || rewinding) {
      return;
    }
    const {sha, targets} = redoTarget;
    setRewinding(true);
    setRewindStatus('Redoing…');
    try {
      await performRewind(sha, {isRedo: true, targets});
    } catch (error) {
      showToast(`Redo failed: ${error.message}`, 'error');
    } finally {
      setRewinding(false);
      setRewindStatus('');
    }
  };

  return (
    <>
      <Frame
        className="history-panel-modal"
        embedded={embedded}
        isOpen={isOpen}
        onClose={onClose}
        title="Project History"
        type="custom"
      >
        <div className="history-panel">
          <div className="history-panel-checkpoint">
            <input
              className="history-checkpoint-input"
              disabled={checkpointing}
              onChange={(e) => setCheckpointMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCheckpoint();
                }
              }}
              placeholder="Checkpoint message (optional)"
              type="text"
              value={checkpointMessage}
            />
            <button
              className="history-checkpoint-btn"
              disabled={checkpointing || !projectId}
              onClick={handleCheckpoint}
              title="Commit everything currently in the working tree as a checkpoint"
              type="button"
            >
              {checkpointing ? 'Checkpointing…' : 'Checkpoint now'}
            </button>
            {redoTarget && (
              <button
                className="history-redo-btn"
                disabled={rewinding}
                onClick={handleRedo}
                title="Redo — jump forward again to before the last rewind"
                type="button"
              >
                {rewinding && rewindStatus === 'Redoing…' ? 'Redoing…' : '↷ Redo'}
              </button>
            )}
          </div>

          <PublishedReleases actorOf={actorOf} formatTime={formatRelativeTime} projectId={projectId} />

          {loading && <div className="history-panel-status">Loading history…</div>}
          {loadError && <div className="history-panel-status history-panel-error">{loadError}</div>}

          {!loading && !loadError && commits.length === 0 && (
            <div className="history-panel-status">No commits yet.</div>
          )}

          {!loading && !loadError && commits.length > 0 && (
            <ul className="history-commit-list">
              {commits.map((commit, index) => {
                const actor = actorOf(commit.actorId);
                const checkpoint = isCheckpoint(commit);
                // The newest checkpoint is the project as it is now: rewinding to it changes nothing.
                const current = checkpoint && commits.findIndex(isCheckpoint) === index;
                return (
                <li className={`history-commit-item${checkpoint ? '' : ' is-remote'}`} key={`${commit.kind || 'commit'}:${commit.sha || 'none'}:${index}`}>
                  <button
                    aria-expanded={checkpoint ? selectedSha === commit.sha : undefined}
                    className="history-commit-main"
                    disabled={!checkpoint}
                    onClick={() => checkpoint && handleSelectCommit(commit.sha)}
                    type="button"
                  >
                    <span className="history-commit-message">{commit.message}</span>
                    <span className="history-commit-meta">
                      {commit.repo && <span className="history-commit-repo">{repoLabel(commit.repo)}</span>}
                      {checkpoint && <span className="history-commit-sha">{shortSha(commit.sha)}</span>}
                      <span className="history-commit-actor" title={[actor.email, actor.id].filter(Boolean).join(' · ')}>{actor.name}</span>
                      <span className="history-commit-time" title={commit.timestamp}>
                        {formatRelativeTime(commit.timestamp)}
                      </span>
                    </span>
                  </button>
                  {checkpoint && !current && (
                    <button
                      className="history-rewind-btn"
                      onClick={() => setConfirmRewind({sha: commit.sha, message: commit.message, timestamp: commit.timestamp})}
                      title="Restore the project to this point"
                      type="button"
                    >
                      Rewind to here
                    </button>
                  )}
                  {current && <span className="history-current-badge">Now</span>}

                  {checkpoint && selectedSha === commit.sha && (
                    <div className="history-diff-view">
                      {diffLoading && <div className="history-panel-status">Loading diff…</div>}
                      {!diffLoading && diffError && <div className="history-panel-status history-panel-error">{diffError}</div>}
                      {!diffLoading && !diffError && (
                        <CommitDiffView
                          actorId={commit.actorId}
                          diffText={diffText}
                          formatTime={formatRelativeTime}
                          resolveActor={actorOf}
                          timestamp={commit.timestamp}
                        />
                      )}
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </Frame>

      <Modal
        cancelText="Cancel"
        confirmText="Rewind"
        isDangerous
        isLoading={rewinding}
        isOpen={Boolean(confirmRewind)}
        message={confirmRewind ? (
          rewinding
            ? rewindStatus
            : `This will restore your project to the state at "${confirmRewind.message}" (${shortSha(confirmRewind.sha)}${confirmRewind.timestamp ? `, ${new Date(confirmRewind.timestamp).toLocaleString()}` : ''}): every service as it was at that moment. This is safe — it creates new commits that restore the old content, changes not saved as a checkpoint yet are saved first, and nothing is deleted from history.`
        ) : ''}
        onCancel={() => !rewinding && setConfirmRewind(null)}
        onConfirm={handleConfirmRewind}
        title="Rewind project?"
        type="confirm"
      />

      <ToastContainer onRemoveToast={removeToast} toasts={toasts} />
    </>
  );
};

HistoryPanel.propTypes = {
  embedded: PropTypes.bool,
  focusSha: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  serviceName: PropTypes.string,
};

HistoryPanel.defaultProps = {
  projectId: null,
  serviceName: null,
};

export default HistoryPanel;
